import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Printer } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { myTimetableQuery } from '@/features/timetable/api'
import { MyCoversCard } from '@/features/timetable/components/MyCoversCard'
import { TodayCard } from '@/features/timetable/components/TodayCard'
import { WeekGrid } from '@/features/timetable/components/WeekGrid'
import { WeekSummary } from '@/features/timetable/components/WeekSummary'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function StaffTimetablePage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('My timetable')
  const { data, isPending, error, refetch } = useQuery(myTimetableQuery())

  const header = (
    <PageHeader
      title={t('My timetable')}
      description={t('Your lessons for the week, with what is on now and next.')}
      actions={
        data?.week ? (
          <Button variant="secondary" leftIcon={<Printer />} onClick={() => window.print()}>
            {t('Print')}
          </Button>
        ) : undefined
      }
    />
  )
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  if (!data.week) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon={<CalendarDays />}
            title={data.linked ? t('No timetable yet') : t('Your login is not linked to a staff record')}
            description={
              data.linked
                ? t('The school has not set a current academic year yet.')
                : t('Ask the school office to link your account to your staff record.')
            }
          />
        </Card>
      </>
    )
  }
  const week = data.week
  return (
    <>
      {header}
      <div className="space-y-6">
        <WeekSummary week={week} />
        <div className="grid items-start gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
          <div className="space-y-6">
            <TodayCard week={week} view="teacher" covers={data.covers} coveredBy={data.coveredBy} />
            <MyCoversCard
              covers={data.covers ?? []}
              coveredBy={data.coveredBy ?? []}
              periods={week.periods}
            />
          </div>
          <Card data-print-document>
            <CardHeader
              title={t('Week')}
              description={t('{{year}} · {{count}} periods a week', {
                year: week.academicYear.name,
                count: week.totalPeriods,
              })}
              icon={<CalendarDays />}
            />
            <div className="p-3 sm:p-4">
              {week.entries.length === 0 ? (
                <EmptyState compact icon={<CalendarDays />} title={t('No lessons assigned to you yet')} />
              ) : (
                <WeekGrid week={week} view="teacher" caption={t('My weekly timetable')} highlightNow />
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}
